import type { DatosAnalisis, MesInforme } from "./analitica";
import { perdidasPorMomento } from "./analitica";

/**
 * Informe de cierre de mes (Robert, 02/11/2026: «los datos que cogería una empresa grande, para
 * avanzar, mejorar y analizar»). Un mes cerrado frente al anterior y a la media de los tres
 * anteriores, con conclusiones escritas. El mes en curso no se compara (estaría a medias).
 */

/** «AAAA-MM-01» del mes de `d`. */
export const mesDeFecha = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;

/** El mes anterior (-1) o siguiente (+1) de «AAAA-MM-01». */
export const moverMes = (mes: string, paso: number) => {
  const [a, m] = mes.split("-").map(Number);
  return mesDeFecha(new Date(a, m - 1 + paso, 1));
};

/** Último día del mes, «AAAA-MM-DD». */
export const finDeMes = (mes: string) => {
  const [a, m] = mes.split("-").map(Number);
  const d = new Date(a, m, 0);
  return `${a}-${String(m).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Por defecto, el último mes cerrado. */
export const mesPorDefecto = (hoy: Date) => moverMes(mesDeFecha(hoy), -1);

export const esMesEnCurso = (mes: string, hoy: Date) => mes === mesDeFecha(hoy);

/** Días de lunes a viernes del mes (en el mes en curso, hasta hoy incluido). */
export const diasLaborables = (mes: string, hoy: Date) => {
  const [a, m] = mes.split("-").map(Number);
  const ultimo = esMesEnCurso(mes, hoy)
    ? hoy.getDate()
    : new Date(a, m, 0).getDate();
  let n = 0;
  for (let d = 1; d <= ultimo; d++) {
    const dia = new Date(a, m - 1, d).getDay();
    if (dia !== 0 && dia !== 6) n++;
  }
  return n;
};

const pct = (a: number, b: number) => (b > 0 ? (a / b) * 100 : null);

/** Las cifras del informe de un mes, ya calculadas. */
export type CifrasMes = {
  clientes: number;
  firmado: number | null;
  cobrado: number | null;
  ventaMedia: number | null;
  /** Clientes / consultorías hechas, %. */
  cierre: number | null;
  diasCiclo: number | null;
  llamadas: number;
  /** % de llamadas que contestan. */
  contacto: number | null;
  citas: number;
  realizadas: number;
  /** Hechas / (hechas + no se presentó), %. */
  asistencia: number | null;
  perdidos: number;
  entrados: number;
  /** Leads del mes que llegan a consultoría, %. */
  leadACita: number | null;
  /** Leads del mes que acaban en cliente, %. */
  leadACliente: number | null;
};

export const cifrasMes = (m: MesInforme): CifrasMes => ({
  clientes: m.clientes,
  firmado: m.firmado,
  cobrado: m.cobrado,
  ventaMedia: m.venta_media,
  cierre: pct(m.clientes, m.realizadas),
  diasCiclo: m.dias_ciclo,
  llamadas: m.llamadas,
  contacto: pct(m.contestadas, m.llamadas),
  citas: m.citas,
  realizadas: m.realizadas,
  asistencia: pct(m.realizadas, m.realizadas + m.no_shows),
  perdidos: m.perdidos,
  entrados: m.entrados,
  leadACita: pct(m.cita, m.entrados),
  leadACliente: pct(m.cliente, m.entrados),
});

/** Media de una cifra en varios meses (sin contar los que no la tienen); null si ninguno. */
export const media = (
  meses: CifrasMes[],
  clave: keyof CifrasMes,
): number | null => {
  const valores = meses
    .map((m) => m[clave])
    .filter((v): v is number => typeof v === "number");
  return valores.length
    ? valores.reduce((s, v) => s + v, 0) / valores.length
    : null;
};

/**
 * Cambio frente a otra cifra: en % para recuentos e importes, en puntos para tasas. null si no
 * se puede comparar o la base es tan pequeña que engaña.
 */
export const cambio = (
  actual: number | null,
  antes: number | null,
  tipo: "recuento" | "tasa",
): { valor: number; unidad: "%" | "pts" } | null => {
  if (actual == null || antes == null) return null;
  if (tipo === "tasa")
    return { valor: Math.round(actual - antes), unidad: "pts" };
  if (antes === 0 || Math.max(actual, antes) < 3) return null;
  return { valor: Math.round(((actual - antes) / antes) * 100), unidad: "%" };
};

export type Conclusion = { tono: "bien" | "mal" | "neutro"; texto: string };

type Traducir = (clave: string, opciones?: object) => string;

const redondo = (n: number) => Math.round(n);

/**
 * Conclusiones del mes, en frases: qué ha mejorado y qué ha empeorado frente al mes anterior (solo
 * con bases que no engañan), dónde se pierden más leads, el motivo principal, el canal que mejor
 * convierte y la mejor hora para llamar. Como mucho 7, lo malo primero.
 */
export const conclusionesMes = ({
  actual,
  anterior,
  enCurso,
  analisis,
  mesInforme,
  t,
  esAdmin,
  nombreCanal,
  nombreDia,
}: {
  actual: CifrasMes;
  anterior: CifrasMes | null;
  enCurso: boolean;
  analisis: DatosAnalisis | null;
  mesInforme: MesInforme;
  t: Traducir;
  esAdmin: boolean;
  nombreCanal: (canal: string) => string;
  nombreDia: (dow: number) => string;
}): Conclusion[] => {
  const malas: Conclusion[] = [];
  const buenas: Conclusion[] = [];
  const neutras: Conclusion[] = [];

  if (anterior && !enCurso) {
    const tasas: [keyof CifrasMes, string, number, number][] = [
      // clave, nombre, base del mes, base del anterior
      ["contacto", "contacto", actual.llamadas, anterior.llamadas],
      ["asistencia", "asistencia", actual.realizadas, anterior.realizadas],
      ["cierre", "cierre", actual.realizadas, anterior.realizadas],
      ["leadACita", "lead_a_cita", actual.entrados, anterior.entrados],
    ];
    for (const [clave, nombre, base, baseAntes] of tasas) {
      const a = actual[clave];
      const b = anterior[clave];
      if (typeof a !== "number" || typeof b !== "number") continue;
      if (Math.min(base, baseAntes) < 5 || Math.abs(a - b) < 5) continue;
      (a > b ? buenas : malas).push({
        tono: a > b ? "bien" : "mal",
        texto: t(`conclusiones.tasa_${a > b ? "sube" : "baja"}`, {
          que: t(`conclusiones.que.${nombre}`),
          antes: redondo(b),
          ahora: redondo(a),
        }),
      });
    }
    const recuentos: [keyof CifrasMes, string][] = [
      ["clientes", "clientes"],
      ["realizadas", "realizadas"],
      ["citas", "citas"],
      ["llamadas", "llamadas"],
      ...(esAdmin
        ? ([["cobrado", "cobrado"]] as [keyof CifrasMes, string][])
        : []),
    ];
    for (const [clave, nombre] of recuentos) {
      const c = cambio(
        actual[clave] as number | null,
        anterior[clave] as number | null,
        "recuento",
      );
      if (!c || Math.abs(c.valor) < 15) continue;
      (c.valor > 0 ? buenas : malas).push({
        tono: c.valor > 0 ? "bien" : "mal",
        texto: t(`conclusiones.recuento_${c.valor > 0 ? "sube" : "baja"}`, {
          que: t(`conclusiones.que.${nombre}`),
          pct: Math.abs(c.valor),
        }),
      });
    }
  }

  // Dónde se pierden más leads del mes (paso con peor conversión, con base suficiente).
  const pasos: [string, number][] = [
    ["entrados", mesInforme.entrados],
    ["gestionados", mesInforme.gestionados],
    ["contactados", mesInforme.contactados],
    ["cita", mesInforme.cita],
    ["realizada", mesInforme.realizada],
    ["propuesta", mesInforme.propuesta],
    ["cliente", mesInforme.cliente],
  ];
  let peor: { paso: string; desde: string; tasa: number } | null = null;
  for (let i = 1; i < pasos.length; i++) {
    const [desde, nDesde] = pasos[i - 1];
    const [paso, n] = pasos[i];
    if (nDesde < 5) continue;
    const tasa = (n / nDesde) * 100;
    if (!peor || tasa < peor.tasa) peor = { paso, desde, tasa };
  }
  if (peor)
    neutras.push({
      tono: "neutro",
      texto: t("conclusiones.se_pierde", {
        desde: t(`conclusiones.pasos.${peor.desde}`),
        paso: t(`conclusiones.pasos.${peor.paso}`),
        pct: redondo(peor.tasa),
      }),
    });

  if (analisis) {
    const perdidas = perdidasPorMomento(analisis.perdidas);
    const total = perdidas.reduce((s, g) => s + g.total, 0);
    const motivos = analisis.perdidas.reduce<Record<string, number>>(
      (acc, f) => ({ ...acc, [f.motivo]: (acc[f.motivo] ?? 0) + f.n }),
      {},
    );
    const principal = Object.entries(motivos).sort((a, b) => b[1] - a[1])[0];
    if (principal && total >= 3)
      neutras.push({
        tono: "neutro",
        texto: t("conclusiones.motivo", {
          motivo: principal[0],
          n: principal[1],
          total,
        }),
      });
    const canal = analisis.canales
      .filter((c) => c.entrados >= 10)
      .map((c) => ({ ...c, tasa: (c.citas / c.entrados) * 100 }))
      .sort((a, b) => b.tasa - a.tasa)[0];
    if (canal && canal.tasa > 0)
      buenas.push({
        tono: "bien",
        texto: t("conclusiones.canal", {
          canal: nombreCanal(canal.canal),
          pct: redondo(canal.tasa),
        }),
      });
    const franja = analisis.franjas
      .filter((f) => f.llamadas >= 5)
      .sort(
        (a, b) =>
          b.contestadas / b.llamadas - a.contestadas / a.llamadas ||
          b.llamadas - a.llamadas,
      )[0];
    if (franja)
      neutras.push({
        tono: "neutro",
        texto: t("conclusiones.franja", {
          dia: nombreDia(franja.dow),
          hora: `${franja.hora}:00`,
          pct: redondo((franja.contestadas / franja.llamadas) * 100),
        }),
      });
  }

  return [...malas, ...buenas, ...neutras].slice(0, 7);
};
